/**
 * Layer 5 — InterpretationEngine  (audited: Sheet29 layer)
 * ─────────────────────────────────────────────────────────────────────────────
 * Deterministic rule-based combination and interpretation logic.
 *
 *   runInterpretationEngine(analysisOutput, referenceData) -> InterpretationOutput
 *
 * STRICT RULES
 *   - NO generative AI. NO LLM calls. NO randomness. NO temperature.
 *   - Interpretation MUST be a pure function of (analysisOutput, referenceData).
 *   - All human-readable text MUST be emitted from frozen fragments stored in
 *     the "R-INTERPRETATION-TEXT" reference table (Sheet29_Text). This file
 *     may only combine, select and order those fragments per the rules in
 *     "R-INTERPRETATION-RULES" (Sheet29_Rules).
 *
 * MOCK path passes existing PoC text through unchanged — it is illustrative,
 * not authoritative, and must be replaced by V2.9.9 rule-driven text.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { SOURCE_STATUS, ENGINE_VERSION } from "./sourceStatus";

/**
 * @typedef {Object} InterpretationOutput
 * @property {"MOCK_POC"|"VALIDATED_V299"} source
 * @property {string} engineVersion
 * @property {Object} summaryCards
 * @property {Object[]} overviewMetrics
 * @property {Object[]} themes            length === 12 in the validated flow
 * @property {Object} numbers             { favorable, unfavorable, usage }
 * @property {Object} wealth              { focus, priority[], watch[], next[] }
 */

const NOT_IMPLEMENTED_V299 =
  "[InterpretationEngine · V2.9.9] Not implemented yet. This layer corresponds " +
  "to the audited Sheet29 deterministic rule-based combination stage. " +
  "Do NOT use AI. All text MUST come from Sheet29_Text via referenceData.";

// ─────────────────────────── MOCK PATH (PoC only) ───────────────────────────

/**
 * MOCK interpretation. Passes analysis rawSections through unchanged.
 * @param {import('./analysisEngine').AnalysisOutput} analysisOutput
 * @param {typeof import('./referenceData').referenceData} _refData
 * @returns {InterpretationOutput}
 */
export function runInterpretationEngineMock(analysisOutput, _refData) {
  const s = analysisOutput.rawSections;
  return {
    source: SOURCE_STATUS.MOCK_POC,
    engineVersion: ENGINE_VERSION.POC_MOCK,
    summaryCards: s.summaryCards,
    overviewMetrics: s.overviewMetrics,
    themes: s.themes,
    numbers: s.numbers,
    wealth: s.wealth,
  };
}

// ─────────────────────────── FUTURE_V299_RULE ────────────────────────────────

/**
 * FUTURE_V299_RULE — reserved.
 *
 * Implementation guidance (do NOT execute yet):
 *   1. For each output field, look up the applicable rules in
 *      referenceData.getTable("R-INTERPRETATION-RULES").
 *   2. For each matched rule, assemble the text from
 *      referenceData.getTable("R-INTERPRETATION-TEXT") fragments.
 *   3. Deterministic ordering only — never use randomness.
 *   4. Return `source: VALIDATED_V299`, `engineVersion: V2.9.9`.
 *
 * @param {import('./analysisEngine').AnalysisOutput} analysisOutput
 * @param {typeof import('./referenceData').referenceData} referenceData
 * @returns {InterpretationOutput}
 */
export function runInterpretationEngineV299(analysisOutput, referenceData) {
  // eslint-disable-line no-unused-vars
  throw new Error(NOT_IMPLEMENTED_V299);
}
